import type { ExerciseCardModel } from './sessionModel'
import { ExerciseCard } from './ExerciseCard'
import './sessionDetail.css'

export type SessionSectionIcon = 'cardio' | 'free' | 'milon'

interface SessionSectionProps {
  title: string
  sub: string
  total: string
  icon: SessionSectionIcon
  items: ExerciseCardModel[]
  baseDelayMs: number
}

/**
 * Section header (icon tile + name + sub + right-aligned total) followed by a
 * staggered `.ex-list` of `ExerciseCard`s (prototype:
 * docs/plans/assets/2026-07-23-session-detail-prototype.html, `.section` /
 * `sectionEl()`). Cards stagger in at `baseDelayMs + 60 + i * 60`, matching
 * the section head's own `baseDelayMs` reveal delay.
 */
export function SessionSection({ title, sub, total, icon, items, baseDelayMs }: SessionSectionProps) {
  const totalLines = total.split('\n')

  return (
    <section className="section">
      <div className="section-head reveal" style={{ '--d': `${baseDelayMs}ms` } as React.CSSProperties}>
        <div className="section-title">
          <span className="section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {ICONS[icon]}
            </svg>
          </span>
          <div>
            <div className="section-name">{title}</div>
            <div className="section-sub">{sub}</div>
          </div>
        </div>
        <div className="section-total">
          {totalLines.map((line, i) => (
            <span key={i}>
              {line}
              {i < totalLines.length - 1 && <br />}
            </span>
          ))}
        </div>
      </div>
      <div className="ex-list">
        {items.map((m, i) => (
          <ExerciseCard key={m.key} model={m} delayMs={baseDelayMs + 60 + i * 60} />
        ))}
      </div>
    </section>
  )
}

const ICONS: Record<SessionSectionIcon, React.ReactNode> = {
  cardio: <path d="M3 12h4l2 6 4-14 2 8h6" />,
  free: <path d="M6.5 6.5v11M17.5 6.5v11M3 9v6M21 9v6M6.5 12h11" />,
  milon: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
}
